<template>
  <div ref="folderElement" class="folder-wrapper" :class="viewClass">
    <div class="folder-container">
      <IconWrapper :icon-height="44" name="folderBlueIcon" />
      <div class="folder-details">
        <div class="folder-details-top">
          <div class="folder-title">
            <NewTooltip
              :class="{ 'title': true, 'hovered-folder': isFolderHovered }"
              placement="top"
              :content="folder.name"
              auto-ellipsis
            />
          </div>
          <slot v-if="props.viewType === FolderViewType.PackageView" name="add-button" />
          <ActionButtons
            v-else-if="props.viewType === FolderViewType.MyVideosView"
            :actions="actions"
            :view-type="DataViewType.Grid"
            class="action-buttons"
            :class="{ 'is-visible': isFolderHovered }"
          />
          <IconWrapperWithOptions
            v-else
            class="pointer delete-icon"
            name="deleteIconBodyText"
            :fill="greyIconColor"
            @click.stop="handleDeleteFolder"
          />
        </div>
        <div class="folder-info">
          <span class="nowrap">{{ folder.number_of_videos }} {{ $t('content-videos') }}</span>
          <span class="bullet-separator">●</span>
          <span>{{ formatDateDateMonthYear(folder.create_date!) }}</span>
        </div>
        <VideoComponentCategories
          v-if="props.viewType === FolderViewType.SelectedFolderInPackage"
          :categories="folderCategories"
          class="categories"
          :class="cSize.breakpointsAdditive"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useElementHover } from '@vueuse/core'
import IconWrapper from '@/components/common/icons/IconWrapper.vue'
import ActionButtons from '@/components/common/action-buttons/ActionButtons.vue'
import NewTooltip from '@/components/common/tooltips/NewTooltip.vue'
import VideoComponentCategories from '@/components/base/video-component-tags/VideoComponentCategories.vue'
import IconWrapperWithOptions from '@/components/common/icons/IconWrapperWithOptions.vue'
import { CRUD, DataType, DataViewType } from '@/interfaces'
import { Folder } from '@/services/folder/types'
import { formatDateDateMonthYear, openModalCustomConfirmation, isDefaultGeneralFolder} from '@/services/utils'
import { ModalType } from '@/components/modals/modals-util'
import { componentSize } from '@/composables'
import { checkPermission } from '@/store/permissions'
import { useFoldersPlaylist } from '@/components/content/videos/folders/useFoldersPlaylist'
import { greyIconColor } from '@/services/constants'
import { FolderViewType } from '@/components/content/videos/folders/types'

const props = withDefaults(
  defineProps<{
    folder: Folder
    viewType?: FolderViewType
  }>(),
  {
    viewType: FolderViewType.MyVideosView
  }
)

const emit = defineEmits(['deleteFolder'])
const folderElement = ref<HTMLElement>()
const isFolderHovered = useElementHover(folderElement)
const cSize = reactive(componentSize())
const router = useRouter()
const store = useStore()
const { getDisabledTooltip, createAddFolderToPlaylist } = useFoldersPlaylist()
const disabledTooltip = getDisabledTooltip(props.folder)
const addFolderToPlaylist = createAddFolderToPlaylist(props.folder, store.state.user.user.organization_id)
const viewClass = computed(() => ({
  'grid-view': props.viewType === FolderViewType.PackageView || props.viewType === FolderViewType.MyVideosView,
  'list-view': props.viewType === FolderViewType.SelectedFolderInPackage
}))
const isDefaultFolder = computed(() => isDefaultGeneralFolder(props.folder.name))
const folderCategories = computed(() => {
  const categoriesSet = new Set()
  props.folder.videos?.forEach((video) => {
    video.categories?.forEach((category) => categoriesSet.add(category))
  })
  return Array.from(categoriesSet)
})
const hasPlaylistPermission = computed(() => checkPermission('playlists'))

const actions = computed(() => {
  const baseActions = [
    {
      label: 'edit',
      handler: openEditModal,
      translationKey: 'edit',
      isDisabled: isDefaultFolder.value
    },
    {
      label: 'delete',
      handler: handleDeleteFolder,
      translationKey: 'delete',
      isDisabled: isDefaultFolder.value
    }
  ]

  if (hasPlaylistPermission.value) {
    return [
      {
        label: 'add',
        handler: addFolderToPlaylist,
        translationKey: 'add-to-playlist',
        isDisabledWithTooltip: disabledTooltip.value
      },
      ...baseActions
    ]
  }

  return baseActions
})

function handleDeleteFolder() {
  if (props.viewType === FolderViewType.SelectedFolderInPackage) {
    emit('deleteFolder', props.folder)
    return
  }
  const modalType = ModalType.ADD_FOLDER_MODAL
  openModalCustomConfirmation({ action: CRUD.Delete, type: DataType.Folder, onConfirm: () => emit('deleteFolder', props.folder) }, modalType)
}

function openEditModal() {
  router.push({
    name: 'edit-folder',
    params: { id: props.folder.id }
  })
}

</script>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.folder-wrapper {
  cursor: pointer;
  border-radius: 12px;
  border: 1px solid $inputBorder;
  background: $white;
  @include hoverFolderEffect;

  .folder-container {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    padding: 1rem;

    .folder-details {
      flex: 1;
      min-width: 0;

      .folder-details-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;

        .folder-title {
          flex: 1;
          overflow: hidden;

          .title {
            font-size: 16px;
            font-weight: 600;
            color: $titleBlack;
          }
        }

        .delete-icon {
          flex-shrink: 0;
        }
      }

      .folder-info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-top: 0.25rem;

        > span {
          font-size: 12px;
          color: $bodyText;
        }

        .bullet-separator {
          font-size: 8px;
        }
      }

      .categories {
        margin-top: 0.5rem;
        height: 22px;
      }
    }
  }

  // Grid view (My Videos + Edit Package)
  &.grid-view {
    width: 315px;

    .icon-wrapper {
      padding: 6px;
      align-self: flex-start;
    }

    .folder-title .title {
      font-size: 18px;
      font-weight: 700;
      max-width: 230px;

      &.hovered-folder {
        max-width: 220px;
      }
    }

    .action-buttons {
      flex: 0;
      padding: 0;
      visibility: hidden;

      &.is-visible {
        visibility: visible;
      }
    }

    .categories {
      margin-top: 0.75rem;
      max-width: 240px;
    }
  }

  // List view (Selected Folder in Package)
  &.list-view {
    width: 100%;
  }

  .action-buttons {
    :deep(.dropdown-img) {
      filter: invert(1);
    }
  }
}
</style>
